import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { BLOG_POSTS } from '../data/blogPosts';
import { SEOHead } from '../components/SEOHead';

export default function BlogPost() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const post = BLOG_POSTS.find(p => p.id === id);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const content = t(post.contentKey);
  const paragraphs = content.split('\n\n').filter(p => p.trim());

  return (
    <>
      <SEOHead
        title={t(post.titleKey)}
        description={t(post.excerptKey)}
        keywords={post.keywords}
        image={post.image}
      />
      <div className="container mx-auto px-6 py-8 md:py-24">
        <article className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('blog.backToBlog')}
            </Link>
          </motion.div>

          <motion.div
            className="aspect-[21/9] rounded-xl overflow-hidden mb-8 bg-gray-custom/10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <img
              src={post.image}
              alt={t(post.titleKey)}
              className="w-full h-full object-cover"
            />
          </motion.div>

          <motion.div
            className="flex items-center gap-4 text-sm text-light/60 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString(currentLang, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{post.readTime} {t('blog.minRead')}</span>
            </div>
          </motion.div>

          <motion.h1
            className="font-tilt-warp text-3xl md:text-4xl lg:text-5xl text-light mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {t(post.titleKey)}
          </motion.h1>

          <motion.div
            className="prose prose-lg max-w-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {paragraphs.map((paragraph, index) => {
              if (paragraph.startsWith('# ')) {
                return (
                  <h2 key={index} className="font-tilt-warp text-2xl md:text-3xl text-light mt-12 mb-4">
                    {paragraph.substring(2)}
                  </h2>
                );
              }
              if (paragraph.startsWith('## ')) {
                return (
                  <h3 key={index} className="font-tilt-warp text-xl md:text-2xl text-light mt-8 mb-3">
                    {paragraph.substring(3)}
                  </h3>
                );
              }
              if (paragraph.startsWith('- ')) {
                const items = paragraph.split('\n').filter(line => line.startsWith('- '));
                return (
                  <ul key={index} className="list-disc list-inside space-y-2 text-light/80 mb-6">
                    {items.map((item, i) => (
                      <li key={i}>{item.substring(2)}</li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={index} className="text-light/80 leading-relaxed mb-6">
                  {paragraph.split('[').map((part, i) => {
                    if (i === 0) return part;
                    const [linkText, rest] = part.split('](');
                    if (!rest) return '[' + part;
                    const [url, afterLink] = rest.split(')');
                    return (
                      <span key={i}>
                        <Link to={url} className="text-primary hover:text-primary/80 underline">
                          {linkText}
                        </Link>
                        {afterLink}
                      </span>
                    );
                  })}
                </p>
              );
            })}
          </motion.div>

          <motion.div
            className="mt-12 panel bg-gray-custom/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <h3 className="font-tilt-warp text-xl text-light mb-4">
              {t('blog.cta.title')}
            </h3>
            <p className="text-light/80 mb-6">
              {t('blog.cta.description')}
            </p>
            <Link
              to="/bands"
              className="btn-primary"
            >
              {t('blog.cta.button')}
            </Link>
          </motion.div>
        </article>
      </div>
    </>
  );
}
